/*
 * ===============================================================================================================
 *                                Copyright 2024, Blue Yonder Group, Inc.
 *                                           All Rights Reserved
 *
 *                               THIS IS UNPUBLISHED PROPRIETARY SOURCE CODE OF
 *                                          BLUE YONDER GROUP, INC.
 *
 *
 *                         The copyright notice above does not evidence any actual
 *                                 or intended publication of such source code.
 *
 * ===============================================================================================================
 */
import _ from "lodash";
import { useDispatch, useSelector } from "react-redux";
import { SetStateAction, useEffect, useRef, useState } from "react";
import {
  ArrowTrendDown,
  Edit,
  Trash,
  PlusCircle,
} from "@jda/lui-common-icon-library-mui5";
import {
  Box,
  Button,
  ListItemText,
  TextField,
  Tooltip,
  Typography,
  IconButton,
} from "@mui/material";
import {
  LuiThreeDotMenu,
  LuiThreeDotMenuThumbnailType,
} from "@jda/lui-common-component-library-mui5";
import { isIFrame } from "@jda/lui-portal-utilities";
import { useStyles } from "./styles";
import { MESSAGES, TITLES } from "./constants";
import Loader from "../../shared/loader/Loader";
import { AlertDialog } from "../../shared/dialog/AlertDialog";
import { selectLoader } from "../../store/loader/loaderSelector";
import AddCustomerDialog from "../../components/addCustomerDialog";
import EmptyComponent from "../../shared/emptyComponent/EmptyComponent";
import SubEnterprise from "../../components/subEnterprise/SubEnterprise";
import { getCustomers, removeCustomer } from "../../store/customer/customer";
import { selectAllAssignedPermissions } from "../../store/auth/authSelector";
import { actions, resources } from "../../components/checkPermission/constants";
import useWindowDimensions from "../../shared/windowDimensions/windowDimensions";
import { PAGE_SIZE_CUSTOMERS } from "../../components/customSearchList/constants";
import CustomSearchList from "../../components/customSearchList/CustomSearchList";
import { setSelectedCustomerAction } from "../../store/customer/customerActionCreator";
import { checkAuthzPermission, CheckAuthzPermissions } from "../../components/checkPermission/CheckViewPermission";
import { selectAllCustomer, selectCustomerPagination, selectNoCustomer, selectSelectedCustomer } from "../../store/customer/customerSelector";

const defaultCustomer = {
  id: "",
  name: "Customers",
  jpowerId: "",
  accountKey: "",
};

const Customer = () => {
  const classes = useStyles();
  const dispatch = useDispatch();

  const { height } = useWindowDimensions();
  const customerDropDownRef: any = useRef(null);

  const isLoading = useSelector(selectLoader);
  const noCustomer = useSelector(selectNoCustomer);
  const customersList = useSelector(selectAllCustomer);
  const pagination = useSelector(selectCustomerPagination);
  const selectedCustomerFromStore = useSelector(selectSelectedCustomer);
  const assignedPermissions = useSelector(selectAllAssignedPermissions);

  const [isEdit, setIsEdit] = useState<boolean>(false);
  const [searchString, setSearchString] = useState("");
  const [showDeleteAlert, setShowDeleteAlert] = useState(false);
  const [openAddCustomer, setOpenAddCustomer] = useState(false);
  const [openSearchCustomer, setOpenSearchCustomer] = useState(false);
  const [selectedCustomer, setSelectedCustomer] = useState(defaultCustomer);


  const createCustomerPermission =
    assignedPermissions &&
    assignedPermissions.resources &&
    checkAuthzPermission(actions.CREATE, assignedPermissions.resources[resources.CUSTOMER]);

  const editCustomerPermission =
    assignedPermissions &&
    assignedPermissions.resources &&
    checkAuthzPermission(actions.EDIT, assignedPermissions.resources[resources.CUSTOMER]);
  const deleteCustomerPermission =
    assignedPermissions &&
    assignedPermissions.resources &&
    checkAuthzPermission(actions.DELETE, assignedPermissions.resources[resources.CUSTOMER]);

  useEffect(() => {
    getAllCustomersList();
  }, []);

  useEffect(() => {
    if (selectedCustomerFromStore) {
      setSelectedCustomer(selectedCustomerFromStore || defaultCustomer);
    }
  }, [selectedCustomerFromStore]);

  const getAllCustomersList = async () => {
    const customerPayload = {
      pageNo: 1,
      searchString: "",
      pageSize: PAGE_SIZE_CUSTOMERS,
    };
    getCustomers(dispatch, customerPayload);
  };

  const handleChangeCustomerSearchInput = (event: any) => {
    const searchValue = event.target.value;
    setSearchString(searchValue);
    const customerPayload = {
      pageNo: 1,
      searchString: searchValue,
      pageSize: PAGE_SIZE_CUSTOMERS,
    };
    if (searchValue.trim() !== "" && searchValue.length > 2) {
      getCustomers(dispatch, customerPayload);
    }
    if (!searchValue.trim().length) {
      getAllCustomersList();
    }
  };

  const handleGoToTheNextPage = (pageNo: number) => {
    const customerPayload = {
      pageNo: pageNo,
      searchString: searchString,
      pageSize: PAGE_SIZE_CUSTOMERS,
    };
    getCustomers(dispatch, customerPayload);
  };

  const onSelectCustomer = (ev, value?: any) => {
    ev.stopPropagation();
    setOpenSearchCustomer(!openSearchCustomer);
    // if (!value) {
    //   // getAllCustomersList();
    // }
    if (value?.id) {
      setSelectedCustomer(value);
      dispatch(setSelectedCustomerAction(value));
    }
  };

  const handleMenuClick = (actionName: string) => {
    if (actionName === "Edit") {
      setIsEdit(true);
      setOpenAddCustomer(true);
    } else {
      setShowDeleteAlert(true);
    }
  };

  const onAddCustomer = async () => {
    setIsEdit(false);
    setOpenAddCustomer(true);
  };

  const deleteCustomer = async () => {
    await removeCustomer(dispatch, selectedCustomer.id);
    setShowDeleteAlert(false);
  };

  const EditThreeDot = (
    <Box className={classes.menuActionWrap}>
      <Edit fontSize="small" />
      <ListItemText className={classes.menuItemText}>
        {TITLES.EDIT}
      </ListItemText>
    </Box>
  );

  const DeleteThreeDot = (
    <Box className={classes.menuActionWrap}>
      <Trash fontSize="small" />
      <ListItemText className={classes.menuItemText}>
        {TITLES.DELETE}
      </ListItemText>
    </Box>
  );

  const menuItems: LuiThreeDotMenuThumbnailType[] = [
    editCustomerPermission && {
      autoClose: true,
      render: EditThreeDot,
      onClick: () => handleMenuClick("Edit"),
    },
    deleteCustomerPermission && {
      autoClose: true,
      render: DeleteThreeDot,
      onClick: () => handleMenuClick("Delete"),
    },
  ];
  const iframeClass = isIFrame() ? classes.isIframe : "";
  return (
    <Box className={`${classes.customerContainer} ${iframeClass}`} height={height}>
      <CheckAuthzPermissions action={actions.VIEW} resource={resources.CUSTOMER}>
        <Loader showLoader={isLoading} isCenter />
        <CheckAuthzPermissions action={actions.CREATE} resource={resources.CUSTOMER}>
          <AddCustomerDialog
            isEdit={isEdit}
            openModal={openAddCustomer}
            customerData={selectedCustomer}
            refreshOnSave={getAllCustomersList}
            openCloseModal={(value: SetStateAction<boolean>) => setOpenAddCustomer(value)}
          />
        </CheckAuthzPermissions>
        {showDeleteAlert ? (
          <AlertDialog
            showDialogLoader={false}
            handleOnOk={deleteCustomer}
            secondButtonText={TITLES.CANCEL}
            recordName={selectedCustomer.name}
            dialogContentText={TITLES.DELETE_CUSTOMER}
            dialogTitle={MESSAGES.DELETE_CUSTOMER_TITLE}
            handleOnCancel={() => setShowDeleteAlert(false)}
            primaryButtonText={TITLES.DELETE_CUSTOMER_CONFIRMATION}
          />
        ) : null}
        <Box className={classes.customerHeader}>
          <Typography className={classes.customerTitle} variant="subtitle1">{TITLES.CUSTOMER}</Typography>

          {openSearchCustomer ? (
            <Box>
              <TextField
                autoFocus
                variant="standard"
                className={classes.customerInput}
                placeholder={TITLES.SEARCH_CUSTOMER}
                onChange={handleChangeCustomerSearchInput}
              />
              <CustomSearchList
                list={customersList}
                selectedItem={selectedCustomer}
                pagination={pagination}
                onSelectItem={onSelectCustomer}
                goToTheNextPage={handleGoToTheNextPage}
                setOpenList={setOpenSearchCustomer}
              />
            </Box>
          ) : (
            <div ref={customerDropDownRef}>
              <Box className={classes.customerHeaderTitle}>
                <Button
                  onClick={onSelectCustomer}
                  data-testid="customer-title"
                  className={classes.customerHeaderTitleWrap}
                >
                  <Tooltip arrow title={selectedCustomer.name} placement="bottom">
                    <Typography className={classes.customerHeaderText}>
                      {selectedCustomer.jpowerId
                        ? `${selectedCustomer.name} (${selectedCustomer.jpowerId})`
                        : selectedCustomer.name}
                    </Typography>
                  </Tooltip>
                  <ArrowTrendDown data-testid="icon-arrow-down" />
                </Button>
                {createCustomerPermission && <Tooltip title={TITLES.ADD_CUSTOMER}>
                  <IconButton
                    aria-label="create"
                    id="createBtn"
                    onClick={onAddCustomer}
                  >
                    <PlusCircle data-testid="customers-plus-icon" />
                  </IconButton>
                </Tooltip>}
                {selectedCustomer.id && (editCustomerPermission || deleteCustomerPermission) && (
                  <Box className={classes.threeDotMenu}>
                    <LuiThreeDotMenu
                      items={_.compact(menuItems)}
                      ariaLabel={"ThreeDotMenu"}
                    />
                  </Box>
                )}
              </Box>
            </div>
          )}
          <Box className={classes.headerRightWrap} />
        </Box>
        <Box className={classes.customerDetailsWrap}>
          <CheckAuthzPermissions action={actions.VIEW} resource={resources.ACCOUNT}>
            {selectedCustomer.id && <SubEnterprise />}
          </CheckAuthzPermissions>
        </Box>
        {!selectedCustomer.id ? (
          <EmptyComponent
            disableCreateAction
            subTitle={MESSAGES.SELECT_CUSTOMER}
            title={MESSAGES.NO_CUSTOMER_SELECTED}
          />
        ) : null}
      </CheckAuthzPermissions>
    </Box>
  );
};

export default Customer;
